/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminOverviewMetricData, IAdminSurveyDistributionData } from '@datatype/admin/api-type';
import { useAdminOverviewMetricQuery } from '@hook/admin/useApi';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import Spinner from '@design-system/spinner';
import Callout from '@design-system/callout';
import {
  TMetricStatus,
  getMetricStatusColor,
  judgeActiveStatus,
  judgeLirStatus,
  judgeD1Status,
  judgeD7Status,
  judgeW1Status,
} from '@constant/admin/metric-threshold';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  width: 280px;
  height: 100%;
  padding: 16px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Header = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid ${color.GRAY_3};
`;

const Card = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
  padding: 10px 12px;
  background-color: ${color.GRAY_4};
  border-radius: 6px;
`;

const LabelRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 6px;
`;

const ValueRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
`;

const StatusChip = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background-color: ${(p) => p.$bg};
  color: ${color.WHITE_1};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const InfoAnchor = styled.div`
  display: inline-flex;
  align-items: center;
`;

const FixedCallout = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  z-index: 1000;
  pointer-events: none;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const SurveyHeader = styled(Header)`
  margin-top: 8px;
`;

const SurveyCard = styled(Card)`
  gap: 10px;
`;

const SurveyChartRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const Pie = styled.div<{ $gradient: string }>`
  flex: 0 0 76px;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: ${(p) => p.$gradient};
`;

const Legend = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
  min-width: 0;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  font-size: 11px;
  color: ${color.BLACK_5};
`;

const LegendDot = styled.span<{ $color: string }>`
  flex: 0 0 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${(p) => p.$color};
`;

const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  overflow: hidden;
  background-color: ${color.GRAY_2};
  border-radius: 3px;
`;

const BarFill = styled.div<{ $width: number; $color: string }>`
  width: ${(p) => p.$width}%;
  height: 100%;
  background-color: ${(p) => p.$color};
`;

const CHART_COLORS = [color.BROWN_1, color.BROWN_2, color.YELLOW_1, color.GREEN_4, color.BLUE_3, color.RED_3];

const SURVEY_TITLES: Record<IAdminSurveyDistributionData['surveyKey'], string> = {
  SOURCE_CHANNEL: '유입 경로',
  EN_STUDY_MOTIVATION: 'EN 학습 목적',
  EN_LANGUAGE_LEVEL: 'EN 학습 단계',
  EN_STUDY_TIME_GOAL: 'EN 목표 시간',
  JA_STUDY_MOTIVATION: 'JA 학습 목적',
  JA_LANGUAGE_LEVEL: 'JA 학습 단계',
  JA_STUDY_TIME_GOAL: 'JA 목표 시간',
};

const SURVEY_ANSWER_LABELS: Record<string, string> = {
  SNS: 'SNS', STORE: '스토어 검색', SEARCH: '인터넷 검색', ARTICLE: '블로그·커뮤니티', REFERRAL: '지인 추천', ETC: '기타',
  HOBBY: '취미', CONVERSATION: '회화', STUDY: '학업', WORK: '업무',
  BEGINNER: '입문', BASIC: '기초', ELEMENTARY: '초급', INTERMEDIATE: '중급', ADVANCED: '고급',
  MINIMAL: '5분', MEDIUM: '10분', CONSIDERABLE: '20분', MAXIMAL: '30분 이상',
};

const SurveyDistribution: React.FC<{ distribution: IAdminSurveyDistributionData }> = ({ distribution }) => {
  const isMultiple = distribution.surveyKey.endsWith('STUDY_MOTIVATION');
  const denominator = distribution.respondentCount || 1;
  let cursor = 0;
  const gradientParts = distribution.answers.map((item, index) => {
    const start = cursor;
    cursor += (item.count / denominator) * 100;
    return `${CHART_COLORS[index % CHART_COLORS.length]} ${start}% ${cursor}%`;
  });
  const gradient = gradientParts.length > 0 ? `conic-gradient(${gradientParts.join(',')})` : color.GRAY_2;
  const AnswerRows = distribution.answers.map((item, index) => {
    const rate = Math.round((item.count / denominator) * 1000) / 10;
    return (
      <Legend key={item.answer}>
        <LegendRow>
          <LegendDot $color={CHART_COLORS[index % CHART_COLORS.length]} />
          <span>{SURVEY_ANSWER_LABELS[item.answer] ?? item.answer}</span>
          <span>{rate}% ({item.count}명)</span>
        </LegendRow>
        {isMultiple && <BarTrack><BarFill $width={rate} $color={CHART_COLORS[index % CHART_COLORS.length]} /></BarTrack>}
      </Legend>
    );
  });

  return (
    <SurveyCard>
      <TextBlock text={SURVEY_TITLES[distribution.surveyKey]} design={{ text: { size: 'small', color: color.BLACK_2, bold: true } }} />
      <TextBlock text={`응답 ${distribution.respondentCount}명${isMultiple ? ' · 복수 선택' : ''}`} design={{ text: { size: 'tiny', color: color.BLACK_5 } }} />
      <SurveyChartRow>
        {!isMultiple && <Pie $gradient={gradient} role='img' aria-label={`${SURVEY_TITLES[distribution.surveyKey]} 파이 차트`} />}
        <Legend>{AnswerRows}</Legend>
      </SurveyChartRow>
    </SurveyCard>
  );
};

const ICON_DESIGN = {
  icon: { size: 'tiny' as const, color: color.BLACK_5, kind: 'outlined' as const },
  box: { event: { cursor: 'pointer' as const } },
} as const;

const formatRate = (achieved: number, target: number): string => {
  if (!target) return '0%';
  return `${Math.round((achieved / target) * 1000) / 10}%`;
};

const formatTtv = (minutes: number): string => {
  if (!minutes) return '-';
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}시간 ${rest}분` : `${hours}시간`;
};

interface IRowProps {
  label: string;
  value: string;
  subValue?: string;
  info: string;
  status?: TMetricStatus;
}

const Row: React.FC<IRowProps> = ({ label, value, subValue, info, status }) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const handleEnter = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({ top: rect.top - 4, left: rect.right + 12 });
  };

  const handleLeave = () => setPosition(null);

  return (
    <Card>
      <LabelRow>
        <TextBlock text={label} design={{ text: { size: 'small', color: color.BLACK_5 } }} />
        <InfoAnchor ref={anchorRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
          <IconBlock icon='info' design={ICON_DESIGN} />
        </InfoAnchor>
      </LabelRow>
      <ValueRow>
        <TextBlock text={value} design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
        {status && <StatusChip $bg={getMetricStatusColor(status)}>{status}</StatusChip>}
      </ValueRow>
      {subValue && <TextBlock text={subValue} design={{ text: { size: 'small', color: color.BLACK_5 } }} />}
      {position &&
        createPortal(
          <FixedCallout $top={position.top} $left={position.left}>
            <Callout text={info} direction='left' theme={ETheme.BLACK} size='tiny' width='240px' />
          </FixedCallout>,
          document.body,
        )}
    </Card>
  );
};

const OverviewPanel: React.FC = () => {
  const { data, isLoading } = useAdminOverviewMetricQuery();
  const overview: IAdminOverviewMetricData | null = data?.data ?? null;

  if (isLoading || !overview) {
    return (
      <Container>
        <Loader>
          <Spinner size='24px' color={color.BROWN_1} />
        </Loader>
      </Container>
    );
  }

  const SurveyCharts = (overview.surveyDistributions ?? []).map((distribution) => (
    <SurveyDistribution key={distribution.surveyKey} distribution={distribution} />
  ));

  return (
    <Container>
      <Header>
        <TextBlock text='마케팅 지표' design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }} />
      </Header>
      <Row
        label='총 회원수'
        value={`${overview.totalMemberCount}명`}
        info='이야기고에 가입한 누적 회원 수입니다. 비회원(체험 학습자)은 제외됩니다.'
      />
      <Row
        label='유료 회원'
        value={`${overview.paidMemberCount}명`}
        subValue={`월간 ${overview.monthlyPlanMemberCount} · 연간 ${overview.yearlyPlanMemberCount}\nApp Store ${overview.appStorePaidMemberCount} · Play Store ${overview.playStorePaidMemberCount}`}
        info='현재 활성 상태인 이야기고 구독 회원 수입니다. 월간·연간 및 결제 플랫폼별 수치는 각각 고유 회원 기준이며, 한 회원이 여러 플랫폼에 활성 구독을 가진 경우 분류별 합계는 전체와 다를 수 있습니다.'
      />
      <Row
        label='활성 회원 (7일)'
        value={`${overview.activeMemberCount}명`}
        subValue={formatRate(overview.activeMemberCount, overview.totalMemberCount)}
        info={
          '최근 7일 안에 빈칸문제를 한 번이라도 학습한 회원 수입니다. WAU(Weekly Active User)에 해당합니다.\n' +
          '· 우수: 30% 이상 / 양호: 20% 이상 / 보통: 10% 이상 / 미흡: 10% 미만'
        }
        status={judgeActiveStatus(overview.activeMemberCount, overview.totalMemberCount)}
      />
      <Row
        label='LIR 달성률'
        value={formatRate(overview.lirAchievedCount, overview.lirTargetCount)}
        subValue={`${overview.lirAchievedCount} / ${overview.lirTargetCount}명`}
        info={
          'LIR(리텐션 선행 지표) = 최근 30일 가입자 중 가입 후 1주 안에 빈칸문제를 2회 이상 학습한 비율. 장기 잔존 가능성을 시사합니다.\n' +
          '· 우수: 40% 이상 / 양호: 25% 이상 / 보통: 15% 이상 / 미흡: 15% 미만'
        }
        status={judgeLirStatus(overview.lirAchievedCount, overview.lirTargetCount)}
      />
      <Row
        label='평균 TTV'
        value={formatTtv(overview.averageTimeToValueMinutes)}
        subValue='가입 → LIR 도달'
        info='TTV(Time-to-Value) = 최근 30일 가입자가 가입 후 LIR(2회차 학습)에 도달하기까지 걸린 평균 시간. LIR과 같은 가치 기준을 공유합니다. 짧을수록 좋습니다.'
      />
      <Row
        label='D1 리텐션'
        value={formatRate(overview.d1RetainedCount, overview.d1TargetCount)}
        subValue={`${overview.d1RetainedCount} / ${overview.d1TargetCount}명`}
        info={
          '최근 30일 가입자 중 가입 다음 날(24~48h) 학습한 비율. 첫인상 평가 지표.\n' +
          '· 우수: 40% 이상 / 양호: 25% 이상 / 보통: 15% 이상 / 미흡: 15% 미만'
        }
        status={judgeD1Status(overview.d1RetainedCount, overview.d1TargetCount)}
      />
      <Row
        label='D7 리텐션'
        value={formatRate(overview.d7RetainedCount, overview.d7TargetCount)}
        subValue={`${overview.d7RetainedCount} / ${overview.d7TargetCount}명`}
        info={
          '최근 30일 가입자 중 가입 7일 후(±1일) 학습한 비율. 일주일 후에도 돌아오는가를 봅니다.\n' +
          '· 우수: 20% 이상 / 양호: 10% 이상 / 보통: 5% 이상 / 미흡: 5% 미만'
        }
        status={judgeD7Status(overview.d7RetainedCount, overview.d7TargetCount)}
      />
      <Row
        label={`W1 리텐션 (${overview.w1MinStudyDays}일+)`}
        value={formatRate(overview.w1RetainedCount, overview.w1TargetCount)}
        subValue={`${overview.w1RetainedCount} / ${overview.w1TargetCount}명`}
        info={
          `최근 30일 가입자 중 가입 후 첫 7일 안에 ${overview.w1MinStudyDays}일 이상 학습한 비율. 학습 습관 형성 여부를 봅니다 (특정 날짜의 점이 아닌, 일주일 동안의 빈도).\n` +
          '· 우수: 25% 이상 / 양호: 15% 이상 / 보통: 8% 이상 / 미흡: 8% 미만'
        }
        status={judgeW1Status(overview.w1RetainedCount, overview.w1TargetCount)}
      />
      <SurveyHeader>
        <TextBlock text='온보딩 설문 분포' design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }} />
      </SurveyHeader>
      {SurveyCharts}
    </Container>
  );
};

export default OverviewPanel;
